import {
  type AnchorSelector,
  type AnchorSlice,
  BLOCK_JOIN,
  buildSelectorFromSlices,
  durableAnchorRefusal,
  isCrossBlock,
  locateSelectorAcrossBlocks,
  locateSelectorInText,
} from "@norkron/web-elaborate-editor";
import { parseBlockAddress } from "@norkron/web-docz";
import type { RangeSlice } from "@norkron/web-page-layout";

/**
 * THE COMMENT BAND MODEL — one comment, N painted ranges.
 *
 * A comment anchored across blocks is, in the docz IR, N blocks each carrying
 * `Mark{.comment, commentId}` over its OWN sub-range (elaborate brief §6 — no new
 * IR concept is needed for a multi-block comment). The canvas has no DOM spans, so
 * the painted band IS the anchor's on-screen presence, and one band per block is
 * what that costs.
 *
 * ⚠ THIS MODULE OWNS NO ANCHOR GEOMETRY. Every derivation below delegates to
 * `@norkron/web-elaborate-editor`: `buildSelectorFromSlices` builds the durable
 * selector (the single-block case is that function with n = 1, byte-identical to
 * `buildSelectorFromOffsets` — pinned upstream, so this consumer has exactly ONE
 * anchoring path and cannot drift), `locateSelectorAcrossBlocks` re-locates a
 * cross-block quote and `locateSelectorInText` a single-block one. What lives here
 * is the ROUTING between them plus the band shape the canvas repaints from.
 *
 * (RULE 13 note: the single/cross dispatch below is itself generic and belongs
 * upstream as one `locateSelector(blocks, selector)`. Filed with the owner of
 * `web-elaborate-editor`; until it lands this stays a deliberately thin adapter so
 * the replacement is a one-line delete.)
 */

/** The shape the canvas document view exposes for every block it paints. */
export interface BandBlock {
  readonly id?: string;
  readonly text?: string;
  readonly kind?: string;
}

/**
 * The block kinds whose text reaches the STORED SECTION BODY as plain text.
 *
 * MEASURED SERVER-SIDE (`src/ingest/html_ingest.zig:212-224` `blockPlainText`):
 * only `heading` / `paragraph` / `text` lower to run text. `list`, `table`,
 * `table_v2`, `block_quote`, `code_block`, `figure`, `image`, `horizontal_rule`
 * and `equation` lower to VERBATIM `htmlz.toHtml` MARKUP. The durable anchor is
 * found by an exact substring search of the quote in that body
 * (`comment_anchors_repo.zig`), so a quote taken from the RENDERED text of a table
 * cell or a list item cannot be found there — no matter how correct the client is.
 */
const BODY_TEXT_KINDS: ReadonlySet<string> = new Set(["heading", "paragraph", "text"]);

/** ONE block's painted contribution to a comment's anchor. */
export interface CommentBand {
  readonly blockId: string;
  readonly start: number;
  readonly end: number;
  /** THIS block's slice of the quote — the string the band re-locates from. */
  readonly exactQuote: string;
}

/** A selector resolved against the CURRENT document view. */
export interface LocatedBands {
  readonly state: "exact" | "shifted";
  readonly bands: readonly CommentBand[];
}

const UTF8 = new TextEncoder();
const UTF8_DEC = new TextDecoder();

/** `[start,end)` UTF-8 bytes of `text`, as text. */
function sliceBytes(text: string, start: number, end: number): string {
  const bytes = UTF8.encode(text);
  const lo = Math.max(0, Math.min(start, bytes.length));
  const hi = Math.max(lo, Math.min(end, bytes.length));
  return UTF8_DEC.decode(bytes.subarray(lo, hi));
}

/**
 * The live selection's per-block slices → the DURABLE selector.
 *
 * `AnchorSlice.text` MUST be the block's FULL text (the affixes and the stored
 * offsets are relative to it), and it MUST come from the same array the offsets
 * were measured against — which is why `view` is passed in rather than re-derived:
 * a `blockOrd` indexes the CANVAS DOCUMENT VIEW, never `handle.blocks`.
 *
 * `null` when the selection is collapsed, or when any covered block has no id (an
 * unaddressable region cannot be named durably — the caller reports that rather
 * than pretending the range does not exist).
 */
export function selectorForSlices(
  view: readonly BandBlock[],
  slices: readonly RangeSlice[],
  baseVersionId?: string,
): AnchorSelector | null {
  if (slices.length === 0) return null;
  const anchorSlices: AnchorSlice[] = [];
  for (const s of slices) {
    const block = view[s.blockOrd];
    if (!block || block.id == null) return null;
    anchorSlices.push({
      blockId: block.id,
      text: block.text ?? "",
      start: s.start,
      end: s.end,
    });
  }
  return buildSelectorFromSlices({ slices: anchorSlices, baseVersionId });
}

/**
 * Why the range covering `slices` could not be re-found in the stored section
 * body, or `null` when it can.
 *
 * Two independent judgements, in the order a reviewer would hit them:
 *
 *  1. **What the SELECTOR knows** — `durableAnchorRefusal` upstream (an empty or
 *     whitespace-only quote has nothing to search for).
 *  2. **What the BLOCK KINDS know** — see {@link BODY_TEXT_KINDS}. A range that
 *     touches a table, a list, a quote, an image or a code block covers text the
 *     body stores as markup. The composer still OPENS for it (the owner's
 *     requirement is explicit), and this string is what it says instead of
 *     pretending the anchor is durable.
 *
 * A sub-block ADDRESS (`b1::li:0`, `b1::td:0:1` — web-docz `block-address.ts`) is
 * the same verdict arrived at from the other side: the id names a REGION inside a
 * container, so its container is one of the markup kinds by construction. It is
 * checked because the canvas projects some regions with a plain `paragraph` kind.
 */
export function anchorRefusal(
  view: readonly BandBlock[],
  slices: readonly RangeSlice[],
  selector: AnchorSelector | null,
): string | null {
  if (!selector) {
    return "This selection can't be named durably — one of its blocks has no id, so a comment on it could not be found again.";
  }
  const fromSelector = durableAnchorRefusal(selector);
  if (fromSelector) return fromSelector;
  const kinds = new Set<string>();
  for (const s of slices) {
    const block = view[s.blockOrd];
    if (!block) continue;
    const id = block.id ?? "";
    const inContainer = id !== "" && parseBlockAddress(id).rootId !== id;
    const kind = block.kind ?? "paragraph";
    if (inContainer || !BODY_TEXT_KINDS.has(kind)) kinds.add(inContainer ? "container" : kind);
  }
  if (kinds.size === 0) return null;
  return `This range covers ${[...kinds].sort().join(", ")} content, which the document stores as markup rather than text — the comment will be created, but it can't re-find itself after the document is edited.`;
}

/** The per-block bands a selector RECORDS, straight from its own decomposition —
 *  used when a server {@link RebaseOutcome} owns the lifecycle and no client-side
 *  re-location may override it. */
export function bandsOfSelector(selector: AnchorSelector): readonly CommentBand[] {
  const parts = selector.exactQuote.split(BLOCK_JOIN);
  if (selector.blocks && selector.blocks.length > 0) {
    return selector.blocks.map((b, i) => ({
      blockId: b.blockId,
      start: b.start,
      end: b.end,
      exactQuote: parts[i] ?? "",
    }));
  }
  return [
    {
      blockId: selector.blockId,
      start: selector.start,
      end: selector.end,
      exactQuote: selector.exactQuote,
    },
  ];
}

/**
 * Re-locate a stored selector in the CURRENT document view and return the bands to
 * paint, or `null` when it is orphaned.
 *
 * A cross-block quote contains {@link BLOCK_JOIN} and therefore can NEVER be found
 * inside one block's text — routing it to the single-block locator was exactly the
 * bug that made every multi-block comment come back ORPHANED after a reload.
 */
export function bandsFromSelector(
  view: readonly BandBlock[],
  selector: AnchorSelector,
): LocatedBands | null {
  const crossBlock = isCrossBlock(selector) || selector.exactQuote.includes(BLOCK_JOIN);
  if (crossBlock) {
    const hit = locateSelectorAcrossBlocks(
      view.map((b) => ({ id: b.id ?? "", text: b.text ?? "" })),
      selector,
    );
    if (!hit) return null;
    return {
      state: hit.state,
      bands: hit.slices.map((s) => {
        const block = view.find((b) => b.id === s.blockId);
        return {
          blockId: s.blockId,
          start: s.start,
          end: s.end,
          exactQuote: sliceBytes(block?.text ?? "", s.start, s.end),
        };
      }),
    };
  }
  const ord = view.findIndex((b) => b.id === selector.blockId);
  if (ord < 0) return null;
  const loc = locateSelectorInText(view[ord]?.text ?? "", selector);
  if (!loc) return null;
  return {
    state: loc.state,
    bands: [
      {
        blockId: selector.blockId,
        start: loc.start,
        end: loc.end,
        exactQuote: selector.exactQuote,
      },
    ],
  };
}

/**
 * Bands → the LIVE `(blockOrd, byte range)` pairs the engine paints, re-located
 * against the current block text on every repaint so a band tracks reflow.
 *
 * A band whose block is gone contributes nothing. A band with an EMPTY quote (a
 * blank cell, a blank paragraph inside the range) keeps its stored offsets clamped
 * into the block: it paints no ink, which is honest — it is a real part of the
 * range with no glyphs in it, not an absence.
 */
export function relocateBands(
  view: readonly BandBlock[],
  bands: readonly CommentBand[],
): { blockOrd: number; start: number; end: number }[] {
  const out: { blockOrd: number; start: number; end: number }[] = [];
  for (const band of bands) {
    const ord = view.findIndex((b) => b.id === band.blockId);
    if (ord < 0) continue;
    const text = view[ord]?.text ?? "";
    if (band.exactQuote.length === 0) {
      const len = UTF8.encode(text).length;
      const at = Math.max(0, Math.min(band.start, len));
      out.push({ blockOrd: ord, start: at, end: at });
      continue;
    }
    const loc = locateSelectorInText(text, band);
    if (!loc) continue;
    out.push({ blockOrd: ord, start: loc.start, end: loc.end });
  }
  return out;
}
